import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  /**
   * Panel max-width on desktop. Accepts a px number or any valid CSS width.
   * Defaults to 480px. Mobile falls back to 92vw regardless.
   */
  width?: number | string;
  /**
   * Optional footer row (sticky at drawer bottom). Useful for primary
   * action buttons so they remain reachable in scrolled detail views.
   */
  footer?: React.ReactNode;
}

/**
 * Canonical drill-down overlay.
 *
 * - Portals to document.body so it is never clipped by ancestor stacking contexts.
 * - Backdrop dims + minor blurs the list behind; click-to-close.
 * - Esc closes. Scroll locks while open.
 * - Slide-in-right on desktop; motion-reduced users get an instant present.
 * - Browser-history aware: opening pushes a `{ pwdDrawer: true }` entry; the
 *   close button unwinds it via `history.back()`; native browser back also
 *   closes the drawer. Result: back button consistently closes the drawer
 *   before navigating the section.
 *
 * Use this for every manager-portal detail view that was previously rendered
 * as an inline sibling panel (the root cause of the "stacked windows" UX).
 */
export default function Drawer({
  open,
  onClose,
  title,
  children,
  width = 480,
  footer,
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // History-aware lifecycle. Pushes a marker entry on open, consumes it on
  // programmatic close, and listens for popstate to detect back-button close.
  useEffect(() => {
    if (!open) return;

    let pushedEntry = false;
    const currentState = (window.history.state as Record<string, unknown> | null) || {};
    if (!currentState.pwdDrawer) {
      window.history.pushState({ ...currentState, pwdDrawer: true }, '', '');
      pushedEntry = true;
    }

    function handlePop(event: PopStateEvent) {
      const state = (event.state as Record<string, unknown> | null) || {};
      if (!state.pwdDrawer) {
        // Our marker is gone — the browser consumed it. Flip open=false via parent.
        pushedEntry = false;
        onCloseRef.current();
      }
    }

    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onCloseRef.current();
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    window.addEventListener('popstate', handlePop);

    // Move focus into the panel for keyboard users.
    const raf = window.requestAnimationFrame(() => {
      panelRef.current?.focus({ preventScroll: true });
    });

    return () => {
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('popstate', handlePop);
      window.cancelAnimationFrame(raf);
      document.body.style.overflow = previousOverflow;

      // If the close came from a non-back source (button/Esc/backdrop), our
      // pushed entry is still on top of the history stack — unwind it so the
      // back stack stays truthful. Skip if popstate already consumed it.
      if (pushedEntry) {
        const topState = (window.history.state as Record<string, unknown> | null) || {};
        if (topState.pwdDrawer) {
          window.history.back();
        }
      }
    };
  }, [open]);

  if (!open) return null;

  const panelStyle: React.CSSProperties = {
    maxWidth: typeof width === 'number' ? `${width}px` : width,
  };

  return createPortal(
    <div className="fixed inset-0 z-[1000]">
      <div
        aria-hidden="true"
        onClick={onClose}
        className="pwd-drawer-backdrop absolute inset-0 bg-black/55"
        style={{ backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)' }}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="pwd-drawer-panel absolute right-0 top-0 h-full w-full bg-[color:var(--color-dark)] border-l border-white/10 shadow-2xl flex flex-col outline-none"
        style={{ ...panelStyle, maxWidth: `min(${panelStyle.maxWidth as string}, 92vw)` }}
      >
        {title ? (
          <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-white/10 bg-[color:var(--color-dark)] px-6 py-4">
            <h2 className="text-h3 truncate">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="btn btn-ghost"
              style={{ padding: '0.25rem 0.625rem' }}
            >
              ✕
            </button>
          </div>
        ) : null}
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer ? (
          <div className="sticky bottom-0 border-t border-white/10 bg-[color:var(--color-dark)] px-6 py-4">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
